import React, {useEffect, useMemo, useState} from 'react';
import {API_BASE_URL} from '../config';
import {authHeader,readApiJson} from '../api/auth';
import './JockeyPerformance.css';

type BestTrack = {name:string; rides:number; wins:number; winRate:number};
type Jockey = {id:string; name:string; rides:number; wins:number; winRate:number|null; pricedRides:number; pricedWins:number; roi:number|null; netUnits:number; bestTrack:BestTrack|null};
type Report = {from:string;to:string;generatedAt:string;commissionRate:number;bestTrackMinimumRides:number;coverage:{resultMarkets:number;matchedRaces:number;rides:number;pricedRides:number;missingBsp:number};jockeys:Jockey[]};
type SortKey = 'rides'|'wins'|'winRate'|'roi'|'bestTrackRate';
const todaySydney=()=>new Intl.DateTimeFormat('en-CA',{timeZone:'Australia/Sydney',year:'numeric',month:'2-digit',day:'2-digit'}).format(new Date());
const pct=(n:number|null)=>n==null?'—':n.toFixed(2)+'%';
const signedPct=(n:number|null)=>n==null?'—':(n>0?'+':'')+n.toFixed(2)+'%';

export default function JockeyPerformance(){
 const [from,setFrom]=useState('2026-06-01');
 const [to,setTo]=useState(todaySydney);
 const [range,setRange]=useState({from:'2026-06-01',to:todaySydney()});
 const [report,setReport]=useState<Report|null>(null);
 const [error,setError]=useState('');
 const [loading,setLoading]=useState(true);
 const [search,setSearch]=useState('');
 const [minimum,setMinimum]=useState(20);
 const [sort,setSort]=useState<SortKey>('rides');
 const [direction,setDirection]=useState<'asc'|'desc'>('desc');
 const [page,setPage]=useState(1);
 useEffect(()=>{
  let cancelled=false;
  setLoading(true);setError('');
  const auth=authHeader();
  const query=new URLSearchParams(range);
  fetch(API_BASE_URL+'/api/results/jockeys?'+query.toString(),{headers:auth})
   .then(r=>readApiJson(r,auth.Authorization))
   .then((r:Report)=>{if(!cancelled){setReport(r);setPage(1)}})
   .catch((e:Error)=>{if(!cancelled)setError(e.message||'Could not load jockey results')})
   .finally(()=>{if(!cancelled)setLoading(false)});
  return ()=>{cancelled=true};
 },[range]);
 const rows=useMemo(()=>{
  const needle=search.trim().toLocaleLowerCase();
  const filtered=(report?.jockeys||[]).filter(j=>j.rides>=minimum&&(!needle||j.name.toLocaleLowerCase().includes(needle)||j.bestTrack?.name.toLocaleLowerCase().includes(needle)));
  const value=(j:Jockey)=>sort==='bestTrackRate'?j.bestTrack?.winRate??-Infinity:j[sort]??-Infinity;
  return filtered.sort((a,b)=>(direction==='desc'?1:-1)*((value(b) as number)-(value(a) as number))||b.rides-a.rides||a.name.localeCompare(b.name));
 },[report,minimum,search,sort,direction]);
 const pageSize=50,totalPages=Math.max(1,Math.ceil(rows.length/pageSize));
 const visible=rows.slice((Math.min(page,totalPages)-1)*pageSize,Math.min(page,totalPages)*pageSize);
 const sortBy=(key:SortKey)=>{setDirection(sort===key&&direction==='desc'?'asc':'desc');setSort(key);setPage(1)};
 const apply=(e:React.FormEvent)=>{e.preventDefault();if(from>to){setError('Start date must be before the end date.');return}setRange({from,to})};
 return <section className="jockey-report">
  <header><div><h1>Jockey performance</h1><p>All matched thoroughbred Win rides from June 2026 · Betfair BSP</p></div></header>
  <form className="jockey-filters" onSubmit={apply}>
   <label>From <input type="date" min="2026-06-01" max={todaySydney()} value={from} onChange={e=>setFrom(e.target.value)}/></label>
   <label>To <input type="date" min={from} max={todaySydney()} value={to} onChange={e=>setTo(e.target.value)}/></label>
   <button type="submit" disabled={loading}>Apply dates</button>
   <label>Jockey or track <input type="search" placeholder="Search" value={search} onChange={e=>{setSearch(e.target.value);setPage(1)}}/></label>
   <label>Minimum rides <select value={minimum} onChange={e=>{setMinimum(Number(e.target.value));setPage(1)}}>{[1,10,20,50,100].map(n=><option key={n} value={n}>{n}</option>)}</select></label>
  </form>
  {error&&<p className="jockey-error" role="alert">{error}</p>}
  {loading&&!report&&<p>Loading jockey results…</p>}
  {report&&<>
   <div className="jockey-summary">
    <div><strong>{report.jockeys.length.toLocaleString()}</strong><span>Jockeys recorded</span></div>
    <div><strong>{report.coverage.rides.toLocaleString()}</strong><span>Verified rides</span></div>
    <div><strong>{report.coverage.matchedRaces.toLocaleString()}</strong><span>Matched races</span></div>
    <div><strong>{report.coverage.pricedRides.toLocaleString()}</strong><span>Rides with BSP</span></div>
   </div>
   <p className="jockey-caption">{report.from} to {report.to} · Showing {rows.length} jockeys. Click a heading to sort. {loading?'Updating…':''}</p>
   <div className="jockey-table-wrap"><table><thead><tr>
    <th>Jockey</th>
    <th><button onClick={()=>sortBy('rides')}>Rides {sort==='rides'?(direction==='desc'?'↓':'↑'):''}</button></th>
    <th><button onClick={()=>sortBy('wins')}>Wins {sort==='wins'?(direction==='desc'?'↓':'↑'):''}</button></th>
    <th><button onClick={()=>sortBy('winRate')}>Win rate {sort==='winRate'?(direction==='desc'?'↓':'↑'):''}</button></th>
    <th><button onClick={()=>sortBy('roi')}>Win ROI {sort==='roi'?(direction==='desc'?'↓':'↑'):''}</button></th>
    <th>BSP rides</th>
    <th>Best track</th>
    <th><button onClick={()=>sortBy('bestTrackRate')}>Track win rate {sort==='bestTrackRate'?(direction==='desc'?'↓':'↑'):''}</button></th>
   </tr></thead><tbody>{visible.map(j=><tr key={j.id}>
    <td className="jockey-name">{j.name}</td><td>{j.rides}</td><td>{j.wins}</td><td>{pct(j.winRate)}</td>
    <td className={j.roi==null?'':j.roi>=0?'positive':'negative'}>{signedPct(j.roi)}</td>
    <td>{j.pricedRides}</td>
    <td>{j.bestTrack?<>{j.bestTrack.name}<small>{j.bestTrack.wins} wins / {j.bestTrack.rides} rides</small></>:'—'}</td>
    <td>{pct(j.bestTrack?.winRate??null)}</td>
   </tr>)}</tbody></table></div>
   {!rows.length&&<p>No jockeys meet these filters.</p>}
   {totalPages>1&&<div className="jockey-pages"><button disabled={page<=1} onClick={()=>setPage(page-1)}>Previous</button><span>Page {Math.min(page,totalPages)} of {totalPages}</span><button disabled={page>=totalPages} onClick={()=>setPage(page+1)}>Next</button></div>}
   <p className="jockey-method"><strong>How this is calculated:</strong> Win rate is wins divided by verified rides. ROI assumes a $1 Win bet on every ride with reconciled Betfair BSP, less 10% commission on positive winnings; {report.coverage.missingBsp.toLocaleString()} matched rides lack a usable BSP and are excluded from ROI. A best track needs at least {report.bestTrackMinimumRides} rides for that jockey; its strike rate and sample are shown together. Results are joined by date, track, race and runner to PuntingForm jockey records. Unmatched races are excluded, so this is the covered sample, not every Australian ride.</p>
  </>}
 </section>;
}
